import { isCancelledError } from "@tanstack/react-query";
import { reportPlatformError } from "$root/api/error-reporting";
import { runWithErrorReporting } from "$root/api/error-reporting/report";
import { getApiGen } from "$root/api/factory";
import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { aclByArea, roleFromAcl } from "$root/functional-areas/acl/checkers/all";
import { spawnBulkShareAction } from "$root/functional-areas/portfolio-studio/bulk-actions/spawn-bulk-share-action";
import { START_DATE_CREATE_PROPOSAL_COMMENTARY } from "$root/functional-areas/const";
import { generateReportBuilderTemplateUrl } from "$root/functional-areas/pdf";
import { waitForEvent } from "$root/event-bus";
import { formatDate } from "$root/localization/formatters";
import { AsOfButton } from "$root/pages/PortfolioDetails/as-of/AsOfButton";
import {
	type TemplateChooserUserPreferenceMap,
	type TemplateChooserUserPreferenceValue,
	TemplateChooserTable,
} from "$root/functional-areas/portfolio-studio/dialogs/TemplateChooserDialog";
import { useCanSendToExecution } from "$root/functional-areas/portfolio/execution";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import {
	type StoryfolioV2DownloadTarget,
	downloadStoryfolioV2Bulk,
} from "$root/functional-areas/storyfolio-v2/download";
import { type ExportTemplate, isStoryfolioV2Template } from "$root/functional-areas/reports/export-templates";
import { getCurrentUser } from "$root/functional-areas/user";
import { platformToast, ToastContent } from "$root/notification-system/toast";
import { exportFailure, rethrowConversionRefusal } from "$root/hooks/useCompositionDownload";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { generateIntentId, trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { queryClientKeys, typedQueryClient } from "$root/third-party-integrations/react-query/typedQueryClient";
import {
	differenceInDaysISO,
	downloadBlob,
	downloadContentDisposition,
	localDateToServerDate,
	parallelize,
	parallelizeWithAllSettled,
	seconds,
	serverYesterdayAsLocalDate,
	ToastableError,
	typeAssertion,
	UserCancelledError,
	utcDateToLocalDate,
} from "$root/utils";
import { spawnDatePickerDialog } from "$root/components/spawnable/date-picker-dialog";
import { computeBulkAcceptInThePastRange } from "./bulk-accept-in-the-past";
import {
	type AsOfTriggerResponse,
	AsOfTriggerResponseStatusEnum,
	type BatchExportRequest,
	type BulkAclEntity,
	InvestmentExportConverterType,
	BatchExportRequestSnapshotEnum,
	IntegrationsControllerV3ApiFactory,
	IntegrationsImportAsOfTriggerControllerApiFactory,
	InvestmentBulkBulkStaticConfigurationControllerV1ApiFactory,
	InvestmentBulkEnhancementConfigurationControllerV4ApiFactory,
	PdfControllerApiFactory,
	type InvestmentProjectedListEntry,
	type InvestmentSummary,
} from "@eu.mdotm.api/sphere";
import { Column, Row, Text } from "@mdotm/shadcn/legacy/components";
import { Radio, RadioGroup } from "@mdotm/shadcn/mdotm/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import type { XOR } from "@mdotm/utils/objects";
import type { MaybePromise } from "@mdotm/utils/promise";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { type RwAtom, WithRwAtom } from "@mdotm/shadcn/legacy/reactivity";
import { noop, switchExpr } from "@mdotm/shadcn/legacy/utils";
import { Map } from "immutable";
import { type ReactNode, useCallback, useMemo } from "react";
import { multipleEntityDelete } from "../common";
import {
	type ApplyTo,
	type DownloadReportReviewState,
	canBeDated,
	earliestInception,
	effectiveApplyTo,
	planBulkReportPages,
	summariseBulkReportPlan,
} from "./bulk-report-as-of";
import {
	type SpawnBulkAnalysisReviewDialogParams,
	BulkAnalysisReviewSections,
	PortfolioActionUnavailableReasonToMessageMap,
	spawnBulkAnalysisReviewDialog,
} from "./BulkAnalysisReviewDialog";
import {
	PortfolioActionUnavailableReason,
	type PortfolioBulkActionAvailabilityResult,
} from "./portfolio-actions.const";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { buildBulkCompositionExportConfig } from "$root/functional-areas/workspace/workspaceTable/composition-export";
import { unreachable } from "@mdotm/utils/runtime";

export const PortfolioListAction = {
	delete: "delete",
	downloadReport: "downloadReport",
	downloadComposition: "downloadComposition",
	exportComposition: "exportComposition",
	compare: "compare",
	createProposal: "createProposal",
	acceptProposal: "acceptProposal",
	acceptProposalInThePast: "acceptProposalInThePast",
	sendToExecution: "sendToExecution",
	stopExecution: "stopExecution",
	rejectProposal: "rejectProposal",
	editSettings: "editSettings",
	generateCommentary: "generateCommentary",
	useAsMixedPortfolio: "useAsMixedPortfolio",
	share: "share",
} as const;

export type PortfolioListAction = (typeof PortfolioListAction)[keyof typeof PortfolioListAction];

type AcceptedOrReadyOrProposalValidationResult =
	| [true, undefined]
	| [false, "ERROR"]
	| [false, "CALCULATING"]
	| [false, "REVIEW"]
	| [false, "RETRIEVING_DATA"]
	| [false, "DRAFT"]
	| [false, "EXECUTING"];

type AcceptedOrReadyValidationResult =
	| [true, undefined]
	| [false, "ERROR"]
	| [false, "CALCULATING"]
	| [false, "REVIEW"]
	| [false, "RETRIEVING_DATA"]
	| [false, "PROPOSAL_READY"]
	| [false, "DRAFT"]
	| [false, "EXECUTING"];

type ProposalValidationResult =
	| [true, undefined]
	| [false, "ACCEPTED"]
	| [false, "READY"]
	| [false, "ERROR"]
	| [false, "CALCULATING"]
	| [false, "RETRIEVING_DATA"]
	| [false, "REVIEW"]
	| [false, "DRAFT"]
	| [false, "EXECUTING"];

function proposalReady(investment: InvestmentSummary): ProposalValidationResult {
	// making sure we cover all states. if the openapi.json enum changes, this line will cause a compilation error
	// allowing us to evaluate the appropriate adjustments.
	typeAssertion<"PROPOSAL_READY" | ProposalValidationResult[1]>(investment.status);
	for (const forbiddenStatus of [
		"ACCEPTED",
		"READY",
		"ERROR",
		"CALCULATING",
		"REVIEW",
		"RETRIEVING_DATA",
		"DRAFT",
		"EXECUTING",
	] as const) {
		if (investment?.status === forbiddenStatus) {
			return [false, forbiddenStatus];
		}
	}
	return [true, undefined];
}

function acceptedOrReady(investment: InvestmentSummary): AcceptedOrReadyValidationResult {
	// making sure we cover all states. if the openapi.json enum changes, this line will cause a compilation error
	// allowing us to evaluate the appropriate adjustments.
	typeAssertion<"ACCEPTED" | "READY" | AcceptedOrReadyValidationResult[1]>(investment.status);
	for (const forbiddenStatus of [
		"ERROR",
		"CALCULATING",
		"REVIEW",
		"RETRIEVING_DATA",
		"PROPOSAL_READY",
		"DRAFT",
		"EXECUTING",
	] as const) {
		if (investment?.status === forbiddenStatus) {
			return [false, forbiddenStatus];
		}
	}
	return [true, undefined];
}

function validateProposalAcceptance(
	investment: InvestmentProjectedListEntry,
	{ canSendToExecution }: { canSendToExecution: boolean },
): ProposalValidationResult | [false, "ACL_EDITOR_REQUIRED"] | [false, "EXECUTION_ONLY"] {
	const user = getCurrentUser();

	if (!aclByArea.portfolio.canEditComposition(user.id, investment.richAcl?.acl ?? [])) {
		return [false, "ACL_EDITOR_REQUIRED"];
	}

	// The status comes first so that a portfolio no accept could touch anyway is reported by its
	// status — the same reason reject gives it — and EXECUTION_ONLY is left to name the one case it
	// alone explains: a proposal that is otherwise ready to accept.
	const result = proposalReady(investment);
	if (!result[0]) {
		return result;
	}

	// With execution available, a live trading portfolio has no Accept anywhere in the product —
	// the detail page swaps that button for Execute — so accepting one here would be the one way
	// left to bypass execution. Without it there is no Execute at all and the detail page keeps
	// showing Accept, so the exclusion stays conditional on it.
	if (canSendToExecution && investment.liveTrading) {
		return [false, "EXECUTION_ONLY"];
	}

	return [true, undefined];
}

export const portfolioListActionValidators = {
	delete(portfolio: InvestmentSummary): [true, undefined] | [false, "ACL_EDITOR_REQUIRED"] | [false, "USED_AS_NESTED"] {
		const user = getCurrentUser();

		if (!aclByArea.portfolio.canDelete(user.id, portfolio?.richAcl?.acl ?? [])) {
			return [false, "ACL_EDITOR_REQUIRED"];
		}
		if ((portfolio?.nofUsagesAsNested ?? 0) > 0) {
			return [false, "USED_AS_NESTED"];
		}
		return [true, undefined];
	},
	downloadReport(investment: InvestmentSummary): AcceptedOrReadyValidationResult {
		return acceptedOrReady(investment);
	},
	downloadComposition(investment: InvestmentSummary): AcceptedOrReadyValidationResult {
		return acceptedOrReady(investment);
	},
	exportComposition(investment: InvestmentSummary): AcceptedOrReadyValidationResult {
		return acceptedOrReady(investment);
	},
	compare(investment: InvestmentSummary): AcceptedOrReadyValidationResult {
		return acceptedOrReady(investment);
	},
	share(investment: InvestmentSummary): [true, undefined] | [false, "ACL_OWNER_REQUIRED"] {
		const user = getCurrentUser();

		if (!investment.richAcl?.acl) {
			return [false, "ACL_OWNER_REQUIRED"];
		}

		if (roleFromAcl("portfolio", user.id, investment.richAcl.acl) !== "OWNER") {
			return [false, "ACL_OWNER_REQUIRED"];
		}

		return [true, undefined];
	},
	createProposal(
		investment: InvestmentProjectedListEntry,
		opts: { isBulk: boolean },
	):
		| AcceptedOrReadyValidationResult
		| [false, "CANNOT_BULK_ENHANCE"]
		| [false, "UNIVERSE_NOT_READY"]
		| [false, "MISSING_UNIVERSE"]
		| [false, "ACL_EDITOR_REQUIRED"] {
		const user = getCurrentUser();

		const result = acceptedOrReady(investment);
		if (!result[0]) {
			return result;
		}
		if (!investment.universeIdentifier) {
			return [false, "MISSING_UNIVERSE"];
		}
		if (opts.isBulk && !investment.canBulkEnhance) {
			return [false, "CANNOT_BULK_ENHANCE"];
		}
		if (opts.isBulk && investment.universeStatus !== "READY") {
			return [false, "UNIVERSE_NOT_READY"];
		}
		if (!aclByArea.portfolio.canCreateProposal(user.id, investment.richAcl?.acl ?? [])) {
			return [false, "ACL_EDITOR_REQUIRED"];
		}

		return [true, undefined];
	},
	acceptProposal(
		investment: InvestmentProjectedListEntry,
		opts: { canSendToExecution: boolean },
	): ProposalValidationResult | [false, "ACL_EDITOR_REQUIRED"] | [false, "EXECUTION_ONLY"] {
		return validateProposalAcceptance(investment, opts);
	},
	// "Accept in the past" has the exact same eligibility as a regular accept.
	acceptProposalInThePast(
		investment: InvestmentProjectedListEntry,
		opts: { canSendToExecution: boolean },
	): ProposalValidationResult | [false, "ACL_EDITOR_REQUIRED"] | [false, "EXECUTION_ONLY"] {
		return validateProposalAcceptance(investment, opts);
	},
	/**
	 * A proposal can only be sent to execution on a live trading portfolio, and only while it is
	 * still pending. `proposalReady` covers the second half, EXECUTING included — which is what stops
	 * the bulk action from toggling *off* a portfolio that is already executing, the endpoint behind
	 * it being a switch rather than a command.
	 */
	sendToExecution(
		investment: InvestmentProjectedListEntry,
	): ProposalValidationResult | [false, "ACL_EDITOR_REQUIRED"] | [false, "PAPER_TRADING"] {
		const user = getCurrentUser();

		// The permission the single-portfolio Execute button guards on. It is checked here, and not
		// left to the server, because a selection made in the list can hold read-only shares.
		if (!aclByArea.portfolio.canCreateProposal(user.id, investment.richAcl?.acl ?? [])) {
			return [false, "ACL_EDITOR_REQUIRED"];
		}

		if (!investment.liveTrading) {
			return [false, "PAPER_TRADING"];
		}

		return proposalReady(investment);
	},
	/**
	 * The other half of the execute-state switch {@link sendToExecution} flips: a portfolio can only
	 * be pulled back out of execution while it is in `EXECUTING`, which is also what keeps this
	 * action from sending anything *to* execution by accident.
	 */
	stopExecution(
		investment: InvestmentProjectedListEntry,
	): [true, undefined] | [false, "ACL_EDITOR_REQUIRED"] | [false, "NOT_EXECUTING"] {
		const user = getCurrentUser();

		// The permission the single-portfolio Stop execution button guards on. It is checked here, and
		// not left to the server, because a selection made in the list can hold read-only shares.
		if (!aclByArea.portfolio.canCreateProposal(user.id, investment.richAcl?.acl ?? [])) {
			return [false, "ACL_EDITOR_REQUIRED"];
		}

		// `EXECUTING` is reachable by live trading portfolios alone, so the status covers the paper
		// trading exclusion too. Every other status is the same one reason — nothing is executing —
		// so the review dialog says it in one line instead of listing the ways of not executing.
		if (investment.status !== "EXECUTING") {
			return [false, "NOT_EXECUTING"];
		}

		return [true, undefined];
	},
	rejectProposal(investment: InvestmentProjectedListEntry): ProposalValidationResult | [false, "ACL_EDITOR_REQUIRED"] {
		const user = getCurrentUser();

		if (!aclByArea.portfolio.canEditComposition(user.id, investment.richAcl?.acl ?? [])) {
			return [false, "ACL_EDITOR_REQUIRED"];
		}

		const result = proposalReady(investment);
		if (!result[0]) {
			return result;
		}

		return [true, undefined];
	},
	editSettings(investment: InvestmentSummary): AcceptedOrReadyValidationResult | [false, "ACL_EDITOR_REQUIRED"] {
		const user = getCurrentUser();

		const result = acceptedOrReady(investment);
		if (!result[0]) {
			return result;
		}

		if (!aclByArea.portfolio.canEditSettings(user.id, investment.richAcl?.acl ?? [])) {
			return [false, "ACL_EDITOR_REQUIRED"];
		}

		return [true, undefined];
	},
	generateCommentary(
		investment: InvestmentProjectedListEntry,
	): AcceptedOrReadyValidationResult | [false, "ACL_EDITOR_REQUIRED"] | [false, "TOO_OLD"] {
		const user = getCurrentUser();

		if (
			investment?.status === "PROPOSAL_READY" &&
			differenceInDaysISO(investment.modificationTime, START_DATE_CREATE_PROPOSAL_COMMENTARY) <= 0
		) {
			return [false, "TOO_OLD"];
		}

		const result = acceptedOrReady(investment);
		if (!result[0]) {
			return result;
		}

		if (!aclByArea.portfolio.canEditComposition(user.id, investment.richAcl?.acl ?? [])) {
			return [false, "ACL_EDITOR_REQUIRED"];
		}

		return [true, undefined];
	},
	useAsMixedPortfolio(
		investment: InvestmentProjectedListEntry,
	): AcceptedOrReadyValidationResult | [false, "CANNOT_MIX"] {
		const result = acceptedOrReady(investment);
		if (!result[0]) {
			return result;
		}

		if (!investment.canUseAsMixedPortfolio) {
			return [false, "CANNOT_MIX"];
		}

		return [true, undefined];
	},
} satisfies Record<
	PortfolioListAction,
	(...args: any[]) => [true, undefined] | [false, PortfolioActionUnavailableReason]
>;

export function checkPortfolioBulkActionAvailability<
	Investment extends InvestmentSummary | InvestmentProjectedListEntry,
>(
	action: PortfolioListAction,
	investments: Investment[],
	opts: { canSendToExecution: boolean },
): PortfolioBulkActionAvailabilityResult<Investment> {
	const available: Investment[] = [];
	const unavailable = noUnavailable<Investment>();

	const validatorOpts = { isBulk: investments.length > 1, canSendToExecution: opts.canSendToExecution };

	for (const investment of investments) {
		const validator = portfolioListActionValidators[action];
		const [valid, reason] = validator(investment, validatorOpts);
		if (valid) {
			available.push(investment);
		} else {
			unavailable[reason].push(investment);
		}
	}

	if (available.length === investments.length) {
		return [available, null];
	}

	return [available, unavailable];
}

export type BulkAnalysisActions = ReturnType<typeof useBulkAnalysisActions>;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useBulkAnalysisActions<Investment extends InvestmentSummary | InvestmentProjectedListEntry>(
	investmentsToValidateParam: Array<Investment>,
	ctx: {
		currentlyComparedInvestments: string[];
		setManualSelection(instrumentUuids: string[]): void;
		setCompareMultipleInvestments(instruments: TemplateChooserUserPreferenceMap): void;
		resetMultiselectCtx(): void;
		onOpenNewTab(investmentsUuids: string[]): string;
		workspaceUid?: string;
	},
) {
	const { push } = useTypedNavigation();
	const canSendToExecution = useCanSendToExecution();
	const ctxRef = useUnsafeUpdatedRef({
		investmentsToValidateParam,
		canSendToExecution,
		setManualSelection: ctx.setManualSelection,
		setCompareMultipleInvestments: ctx.setCompareMultipleInvestments,
		resetMultiselectCtx: ctx.resetMultiselectCtx,
		onOpenNewTab: ctx.onOpenNewTab,
	});

	const onReview = useCallback(
		(unavailable: Record<PortfolioActionUnavailableReason, Investment[]>, reviewMode: "current" | "blank") => {
			switch (reviewMode) {
				case "blank":
					window.open(
						ctxRef.current.onOpenNewTab(
							Object.values(unavailable).flatMap((investments) => investments.map((investment) => investment.uuid!)),
						),
						"_blank",
						"opener",
					);
					break;

				case "current":
					ctxRef.current.setManualSelection(
						Object.values(unavailable).flatMap((investments) => investments.map((investment) => investment.uuid!)),
					);
					break;
			}
		},
		[ctxRef],
	);

	const onActionStarted = useCallback(
		({
			toastTitle,
			toastSubtitle = "Sphere has taken over your request",
			unavailable,
			reviewMode,
		}: {
			toastTitle: string;
			toastSubtitle?: string | null;
			unavailable: Record<PortfolioActionUnavailableReason, Investment[]> | null;
			reviewMode: "current" | null;
		}) => {
			platformToast({
				children: (
					<ToastContent alignIcon={toastSubtitle ? "top" : "center"}>
						<Column gap={8} flexGrow={0}>
							<div>
								<Text as="div" type="Body/L/Bold">
									{toastTitle}
								</Text>
								<div>{toastSubtitle}</div>
							</div>
							{reviewMode && unavailable && (
								<Row gap={16}>
									<button
										type="button"
										className="underline"
										onClick={(e) => {
											e.stopPropagation();
											onReview(unavailable, reviewMode);
										}}
									>
										Review unsuitable
									</button>
								</Row>
							)}
						</Column>
					</ToastContent>
				),
				icon: "Portfolio",
				severity: "success",
			});
			ctxRef.current.resetMultiselectCtx();
		},
		[ctxRef, onReview],
	);

	const spawnCustomizedBulkAnalysisReviewDialogWithException = useCallback(
		async function <T>(
			params: SpawnBulkAnalysisReviewDialogParams<T> & { onContinue(value: T): MaybePromise<void> },
			unavailable: Record<PortfolioActionUnavailableReason, Investment[]> | null,
		): Promise<T> {
			const dialogResult = await spawnBulkAnalysisReviewDialog(params);
			switch (dialogResult.kind) {
				case "review":
					onReview(unavailable!, "current");
					throw new UserCancelledError();
				case "cancel":
					throw new UserCancelledError();
				case "continue":
					return dialogResult.result;
				default:
					unreachable(dialogResult);
			}
		},
		[onReview],
	);

	return useMemo(() => {
		// Read through the ref at call time, not at render time: the customer's integration is fetched
		// asynchronously and can land after the menu has already been built.
		const executionOpts = () => ({ canSendToExecution: ctxRef.current.canSendToExecution });

		const refetchPortfolios = () => {
			typedQueryClient().invalidateQueries([queryClientKeys.portfolioStudioProjectedInvestmentList]).catch(noop);
			portfolioRepository.invalidateLists();
			workspaceRepository.invalidate(workspaceRepository.getWorkspaceData.fullQueryKey);
		};

		/** One failure message for a batch, told to the user and reported to the platform as one event. */
		const reportBulkFailure = (errorMsg: string, attemptedOperation: string, payload: object) => {
			reportPlatformError(new Error(errorMsg), "ERROR", "portfolio", {
				message: attemptedOperation,
				payload: JSON.stringify(payload),
			});
			platformToast({ children: errorMsg, icon: "Portfolio", severity: "error" });
		};

		/**
		 * Tells the user how many portfolios were left out at `asOf`: refused by the snapshot trigger, or
		 * still generating when the wait for them ran out.
		 */
		const reportRefusedSnapshots = (
			refused: AsOfTriggerResponse[],
			asOf: string | null,
			attemptedOperation: string,
			notReady: string[] = [],
		) => {
			const left = refused.length + notReady.length;
			if (asOf == null || left === 0) {
				return;
			}
			const day = formatDate(asOf, { timezone: "UTC" });
			reportBulkFailure(
				left === 1 ? `Unable to export 1 portfolio as of ${day}` : `Unable to export ${left} portfolios as of ${day}`,
				attemptedOperation,
				{ refused, notReady },
			);
		};

		/**
		 * The tail an action shares with every other: run it outright when the whole selection is
		 * eligible, otherwise put the split in front of the user and run it once they confirm.
		 */
		const reviewThenRun = async (
			title: string,
			availabilityResult: PortfolioBulkActionAvailabilityResult<Investment>,
			onContinue: () => void | Promise<void>,
		) => {
			const [available, unavailable] = availabilityResult;
			if (unavailable == null) {
				await onContinue();
				return;
			}
			spawnCustomizedBulkAnalysisReviewDialogWithException(
				{
					initialState: null,
					header: `${title}: review and confirm`,
					children: (
						<BulkAnalysisReviewSections
							bulkActionAvailabilityResult={availabilityResult}
							getRedirectUrl={ctxRef.current.onOpenNewTab}
						/>
					),
					canConfirm: available.length > 0,
					onContinue,
				},
				unavailable,
			).catch(noop);
		};

		/**
		 * The review of a bulk export that can be taken as of a date. Every selection reviews first, a single
		 * eligible portfolio included: the dialog is where the date is picked. An undated copy reviews the same
		 * export with no date to pick.
		 */
		const reviewAsOf = (
			header: string,
			mode: AsOfReviewMode,
			selection: { available: Investment[]; proposalReady: Investment[] },
			unavailable: Record<PortfolioActionUnavailableReason, Investment[]> | null,
			onContinue: (state: DownloadReportReviewState) => Promise<void>,
		) => {
			const initialState: DownloadReportReviewState = { applyTo: "CURRENT", asOf: null };
			spawnCustomizedBulkAnalysisReviewDialogWithException(
				{
					initialState,
					header,
					children: (stateStore$) => (
						<AsOfReviewBody
							stateStore$={stateStore$}
							selection={selection}
							unavailable={unavailable}
							getRedirectUrl={ctxRef.current.onOpenNewTab}
							{...mode}
						/>
					),
					canConfirm: (state) => planBulkReportPages(selection, state).length > 0,
					onContinue,
				},
				unavailable,
			).catch(noop);
		};

		/**
		 * Send to execution and stop execution are one switch flipped either way: one request carries the
		 * whole batch, a per-portfolio failure comes back in `executionErrors` without stopping the others,
		 * and both paths refresh because an aborted batch can still have moved some of the selection.
		 */
		const runExecuteStateFlip = async (
			action: "sendToExecution" | "stopExecution",
			title: string,
			attemptedOperation: string,
			failureMessage: (count: number) => string,
		) => {
			const investmentsToValidate = ctxRef.current.investmentsToValidateParam;
			const availabilityResult = checkPortfolioBulkActionAvailability(action, investmentsToValidate, executionOpts());
			const [available, unavailable] = availabilityResult;
			const onContinue = () =>
				runWithErrorReporting(
					async () => {
						trackMixPanelEvent(`portfolio.bulkActions.${action}`, { selectedPortfolios: available.length });
						onActionStarted({ toastTitle: title, unavailable, reviewMode: null });
						const { executionErrors } = await portfolioRepository
							.bulkUpdateExecuteState(available.map(({ uuid }) => uuid!))
							.finally(refetchPortfolios);

						if (executionErrors.length > 0) {
							reportBulkFailure(failureMessage(executionErrors.length), attemptedOperation, { executionErrors });
						}
					},
					{
						area: "portfolio",
						attemptedOperation: { message: attemptedOperation, payload: JSON.stringify({ available }) },
					},
				);
			await reviewThenRun(title, availabilityResult, onContinue);
		};

		return {
			async acceptProposal() {
				const investmentsToValidate = ctxRef.current.investmentsToValidateParam;
				const availabilityResult = checkPortfolioBulkActionAvailability(
					"acceptProposal",
					investmentsToValidate,
					executionOpts(),
				);
				const [available, unavailable] = availabilityResult;
				const onContinue = () =>
					runWithErrorReporting(
						async () => {
							trackMixPanelEvent("portfolio.bulkActions.acceptProposal", { selectedPortfolios: available.length });
							onActionStarted({
								toastTitle: "Accept proposal",
								unavailable,
								reviewMode: null,
							});
							// One request for the whole batch; a per-portfolio failure comes back in
							// `acceptErrors` without stopping the others. Refresh either way: an aborted
							// batch can still leave portfolios accepted.
							const { acceptErrors } = await portfolioRepository
								.bulkAcceptEnhancement(available.map(({ uuid }) => uuid!))
								.finally(refetchPortfolios);

							if (acceptErrors.length > 0) {
								reportBulkFailure(
									acceptErrors.length === 1
										? `Unable to accept ${acceptErrors.length} proposal`
										: `Unable to accept ${acceptErrors.length} proposals`,
									"try to accept proposal",
									{ acceptErrors },
								);
							}
						},
						{
							area: "portfolio",
							attemptedOperation: {
								message: "try to accept proposal",
								payload: JSON.stringify({ available }),
							},
						},
					);
				await reviewThenRun("Accept proposal", availabilityResult, onContinue);
			},
			async acceptProposalInThePast() {
				const investmentsToValidate = ctxRef.current.investmentsToValidateParam;
				// Eligibility is identical to a regular accept.
				const availabilityResult = checkPortfolioBulkActionAvailability(
					"acceptProposalInThePast",
					investmentsToValidate,
					executionOpts(),
				);
				const [available, unavailable] = availabilityResult;

				const pickDateAndAccept = () =>
					runWithErrorReporting(
						async () => {
							// The picked date must be valid for every portfolio in the batch; the backend
							// computes that batch-wide lower bound in a single call. It isn't on the projected
							// list entry the bulk bar holds, so fetch it lazily — only once "in the past" is
							// chosen.
							const { latestProposalDate } = await portfolioRepository.acceptLowerBound.fetchQuery(
								available.map(({ uuid }) => uuid!),
							);
							const { minDate, maxDate, isEmpty } = computeBulkAcceptInThePastRange(
								latestProposalDate ? utcDateToLocalDate(latestProposalDate) : undefined,
								serverYesterdayAsLocalDate(),
							);
							if (isEmpty) {
								platformToast({
									children:
										"The most recent proposal in this selection was created today and cannot be accepted in the past.",
									icon: "Portfolio",
									severity: "error",
								});
								return;
							}

							const acceptAsLocalDate = await spawnDatePickerDialog({
								header: "Accept in the past",
								confirmLabel: "Accept",
								defaultValue: maxDate,
								minDate,
								maxDate,
								children: (
									<Text as="div" type="Body/M/Book">
										Choose a date between the most recent proposal creation and yesterday.
									</Text>
								),
							});
							if (acceptAsLocalDate == null) {
								return; // dismissed without picking a date
							}
							const acceptAsDate = localDateToServerDate(acceptAsLocalDate);

							trackMixPanelEvent("portfolio.bulkActions.acceptProposal", {
								selectedPortfolios: available.length,
								inThePast: true,
							});
							onActionStarted({ toastTitle: "Accept in the past", unavailable, reviewMode: null });
							// The whole batch is accepted at the shared date in a single request; a
							// per-portfolio failure comes back in `acceptErrors` without stopping the others.
							// Refresh either way: an aborted batch can still leave portfolios accepted.
							const { acceptErrors } = await portfolioRepository
								.bulkAcceptEnhancement(
									available.map(({ uuid }) => uuid!),
									acceptAsDate,
								)
								.finally(refetchPortfolios);

							if (acceptErrors.length > 0) {
								reportBulkFailure(
									acceptErrors.length === 1
										? `Unable to accept ${acceptErrors.length} proposal`
										: `Unable to accept ${acceptErrors.length} proposals`,
									"try to accept proposal in the past",
									{ acceptErrors },
								);
							}
						},
						{
							area: "portfolio",
							attemptedOperation: {
								message: "try to accept proposal in the past",
								payload: JSON.stringify({ available }),
							},
						},
					);

				if (unavailable == null) {
					await pickDateAndAccept();
				} else {
					// Review & confirm first; it closes before the date picker opens (sequential, not stacked).
					try {
						await spawnCustomizedBulkAnalysisReviewDialogWithException(
							{
								initialState: null,
								header: "Accept in the past: review and confirm",
								children: (
									<BulkAnalysisReviewSections
										bulkActionAvailabilityResult={availabilityResult}
										getRedirectUrl={ctxRef.current.onOpenNewTab}
									/>
								),
								canConfirm: available.length > 0,
								onContinue: noop,
							},
							unavailable,
						);
					} catch (err) {
						if (err instanceof UserCancelledError) {
							return; // cancelled or sent to review
						}
						throw err;
					}
					await pickDateAndAccept();
				}
			},
			async sendToExecution() {
				await runExecuteStateFlip(
					"sendToExecution",
					"Send to execution",
					"try to send proposal to execution",
					(count) => `Unable to send ${count} ${count === 1 ? "portfolio" : "portfolios"} to execution`,
				);
			},
			async stopExecution() {
				await runExecuteStateFlip(
					"stopExecution",
					"Stop execution",
					"try to stop the execution of a proposal",
					(count) => `Unable to stop execution on ${count} ${count === 1 ? "portfolio" : "portfolios"}`,
				);
			},
			async rejectProposal() {
				const investmentsToValidate = ctxRef.current.investmentsToValidateParam;
				const availabilityResult = checkPortfolioBulkActionAvailability(
					"rejectProposal",
					investmentsToValidate,
					executionOpts(),
				);
				const [available, unavailable] = availabilityResult;
				const onContinue = () =>
					runWithErrorReporting(
						async () => {
							trackMixPanelEvent("portfolio.bulkActions.rejectProposal", { selectedPortfolios: available.length });
							onActionStarted({ toastTitle: "Reject proposal", unavailable, reviewMode: null });
							const failed: Array<{ err: unknown; portfolioUuid: string }> = [];
							const investmentsProposalToReject = available.map(
								({ uuid }) =>
									() =>
										portfolioRepository
											.rejectEnhancement(uuid!)
											.catch((err) => failed.push({ err, portfolioUuid: uuid! })),
							);

							await parallelizeWithAllSettled(investmentsProposalToReject, { concurrency: 3 });

							if (failed.length < investmentsProposalToReject.length) {
								refetchPortfolios();
							}

							if (failed.length > 0) {
								reportBulkFailure(
									failed.length === 1
										? `Unable to reject ${failed.length} proposal`
										: `Unable to reject ${failed.length} proposals`,
									"try to reject proposal",
									{ failed },
								);
							}
						},
						{
							area: "portfolio",
							attemptedOperation: {
								message: "try to reject proposal",
								payload: JSON.stringify({ available }),
							},
						},
					);
				await reviewThenRun("Reject proposal", availabilityResult, onContinue);
			},
			async createProposal() {
				const intentId = generateIntentId();

				const investmentsToValidate = ctxRef.current.investmentsToValidateParam;
				const availabilityResult = checkPortfolioBulkActionAvailability(
					"createProposal",
					investmentsToValidate,
					executionOpts(),
				);
				const [available, unavailable] = availabilityResult;

				if (investmentsToValidate.length === 1 && available.length === 1) {
					trackMixPanelEvent("portfolio.bulkActions.createProposalIntent", {
						selectedPortfolios: 1,
						intentId,
					});
					if (ctx.workspaceUid) {
						push("Portfolios/EditPortfolioWithWorkspace", {
							intentId,
							portfolioUid: available[0].uuid!,
							workspaceUid: ctx.workspaceUid,
						});
					} else {
						push("Portfolios/EditPortfolio", { intentId, portfolioUid: available[0].uuid! });
					}
					ctxRef.current.resetMultiselectCtx();
				} else {
					const onContinue = () =>
						runWithErrorReporting(
							async () => {
								if (available.length === 1) {
									trackMixPanelEvent("portfolio.bulkActions.createProposalIntent", {
										selectedPortfolios: 1,
										intentId,
									});
									if (ctx.workspaceUid) {
										push("Portfolios/EditPortfolioWithWorkspace", {
											intentId,
											portfolioUid: available[0].uuid!,
											workspaceUid: ctx.workspaceUid,
										});
									} else {
										push("Portfolios/EditPortfolio", { intentId, portfolioUid: available[0].uuid! });
									}
									ctxRef.current.resetMultiselectCtx();
								} else {
									try {
										trackMixPanelEvent("portfolio.bulkActions.createProposalIntent", {
											selectedPortfolios: available.length,
											intentId,
										});
										const { uuid } = await axiosExtract(
											getApiGen(
												InvestmentBulkEnhancementConfigurationControllerV4ApiFactory,
											).createDraftBulkEnhancement(available.map((investment) => investment.uuid!)),
										);
										if (ctx.workspaceUid) {
											push("Portfolios/EditPortfolioMultiWithWorkspace", {
												workspaceUid: ctx.workspaceUid,
												proposalUuid: uuid ?? "",
												intentId,
											});
										} else {
											push("Portfolios/EditPortfolioMulti", { proposalUuid: uuid ?? "", intentId });
										}
										onActionStarted({
											toastTitle: "Create proposal",
											toastSubtitle: null,
											unavailable,
											reviewMode: null,
										});
									} catch (error) {
										throw new ToastableError("Unable to create a proposal", { cause: error, icon: "Portfolio" });
									}
								}
							},
							{ area: "portfolio", attemptedOperation: { message: "try to create bulk enhancement" } },
						);
					await reviewThenRun("Create proposal", availabilityResult, onContinue);
				}
			},
			async downloadReport(template: ExportTemplate) {
				const investmentsToValidate = ctxRef.current.investmentsToValidateParam;
				const availabilityResult = checkPortfolioBulkActionAvailability(
					"downloadReport",
					investmentsToValidate,
					executionOpts(),
				);
				const [available, unavailable, proposalReadyInvestments] = splitProposalReady(availabilityResult);
				const selection = { available, proposalReady: proposalReadyInvestments };
				const everyone = [...available, ...proposalReadyInvestments];

				if (isStoryfolioV2Template(template)) {
					// One batch for the whole selection. A portfolio with a ready proposal is offered the same
					// Current / Proposal / Both choice as a Report Builder template, and each entry carries the stage it
					// is asked on.
					const withUuid = {
						available: available.filter(({ uuid }) => uuid),
						proposalReady: proposalReadyInvestments.filter(({ uuid }) => uuid),
					};
					const onContinue = async (state: DownloadReportReviewState) => {
						const targets = planBulkReportPages(withUuid, state).map(
							({ investment: { uuid, name }, choice }): StoryfolioV2DownloadTarget => ({
								uuid: uuid!,
								name,
								choice,
							}),
						);
						if (targets.length === 0) {
							ctxRef.current.resetMultiselectCtx();
							return;
						}
						await runWithErrorReporting(
							async () => {
								trackMixPanelEvent("portfolio.bulkActions.downloadReport", {
									selectedPortfolios: withUuid.available.length + withUuid.proposalReady.length,
								});
								onActionStarted({ toastTitle: "Download", unavailable, reviewMode: null });
								try {
									const { failed } = await downloadStoryfolioV2Bulk(template, targets);
									// The archive holds whatever was produced, so a partial batch is still delivered —
									// and the documents missing from it are named rather than silently absent.
									if (failed.length > 0) {
										// A portfolio asked for on its proposal is named with the choice that failed.
										const withProposal = new Set(
											targets.filter(({ choice }) => choice === "enhance").map(({ uuid }) => uuid),
										);
										const label = ({ name, uuid, choice }: (typeof failed)[number]) =>
											`${name ?? uuid}${withProposal.has(uuid) ? ` (${choice === "enhance" ? "proposal" : "current"})` : ""}`;
										platformToast({
											icon: "Portfolio",
											severity: "warning",
											children: `No document was produced for: ${failed.map(label).join(", ")}`,
										});
									}
								} catch (error) {
									throw new ToastableError("Unable to download reports", {
										cause: error,
										icon: "Portfolio",
									});
								}
							},
							{ area: "portfolio", attemptedOperation: { message: "try to download report" } },
						);
						ctxRef.current.resetMultiselectCtx();
					};

					if (unavailable == null && withUuid.proposalReady.length === 0) {
						await onContinue({ applyTo: "CURRENT", asOf: null });
						return;
					}
					reviewAsOf(
						"Download: review and confirm",
						{ undatedCopy: STORYFOLIO_V2_REVIEW_COPY },
						withUuid,
						unavailable,
						onContinue,
					);
					return;
				}

				const onContinue = (state: DownloadReportReviewState): Promise<void> =>
					runWithErrorReporting(
						async () => {
							trackMixPanelEvent("portfolio.bulkActions.downloadReport", {
								selectedPortfolios: everyone.length,
							});
							const { asOf } = state;
							const plan = planBulkReportPages(selection, state);
							let pages = plan.flatMap(({ investment: { uuid, name }, choice, asOf }) =>
								generateReportBuilderTemplateUrl([{ choice, uuid: uuid!, name }], template, { asOf }),
							);
							try {
								// Every dated snapshot is commissioned up front, in one call: the render job waits on them one
								// page at a time with a fixed budget each, so a cold snapshot has to be generating before the
								// job reaches its page. A portfolio the trigger refuses is dropped rather than sent to fail there.
								let refused: AsOfTriggerResponse[] = [];
								const dated = plan.filter((page) => page.choice === "current" && page.asOf != null);
								if (asOf != null && dated.length > 0) {
									({ refused } = await commissionSnapshots(
										dated.map(({ investment }) => investment.uuid!),
										asOf,
									));
									const refusedUuids = new Set(refused.map((response) => response.asOfTrigger?.uuid));
									pages = pages.filter((page) => !(page.asOfDate && refusedUuids.has(page.objectId)));
								}
								if (pages.length === 1) {
									onActionStarted({ toastTitle: "Download", unavailable, reviewMode: null });
									const [page] = pages;
									const pdf = await axiosExtract(
										getApiGen(PdfControllerApiFactory).generateCustomReportPdf(page.name, page, {
											responseType: "blob",
										}),
									);
									downloadBlob(pdf as Blob, {
										fileName: page.name ?? "untitled",
									});
								} else if (pages.length > 1) {
									await getApiGen(PdfControllerApiFactory).generateCustomReportsPdf({
										pages,
										templateType: "TPLPortraitNoMargin",
									});
									onActionStarted({ toastTitle: "Download", unavailable, reviewMode: null });
								}
								reportRefusedSnapshots(refused, asOf, "try to download report as of a date");
							} catch (error) {
								throw new ToastableError("Unable to download reports", {
									cause: error,
									icon: "Portfolio",
								});
							}
						},
						{ area: "portfolio", attemptedOperation: { message: "try to download report" } },
					);

				reviewAsOf("Download: review and confirm", { copy: REPORT_REVIEW_COPY }, selection, unavailable, onContinue);
			},
			async downloadComposition(converter: InvestmentExportConverterType) {
				const investmentsToValidate = ctxRef.current.investmentsToValidateParam;
				const availabilityResult = checkPortfolioBulkActionAvailability(
					"downloadComposition",
					investmentsToValidate,
					executionOpts(),
				);
				const [available, unavailable, proposalReadyInvestments] = splitProposalReady(availabilityResult);
				const selection = { available, proposalReady: proposalReadyInvestments };

				const onContinue = async (state: DownloadReportReviewState): Promise<void> => {
					trackMixPanelEvent("portfolio.bulkActions.downloadComposition", {
						selectedPortfolios: available.length + proposalReadyInvestments.length,
					});
					const { asOf } = state;
					let batchExportConfig: BatchExportRequest[] = planBulkReportPages(selection, state).map(
						({ investment: { uuid }, choice, asOf }) =>
							choice === "current"
								? { uuid, snapshot: BatchExportRequestSnapshotEnum.Current, ...(asOf ? { asOfDate: asOf } : {}) }
								: { uuid, snapshot: BatchExportRequestSnapshotEnum.Enhancement },
					);
					// Counted in portfolios, not requests: one portfolio with its proposal is still one download.
					const portfoliosIn = (config: BatchExportRequest[]) => new Set(config.map(({ uuid }) => uuid)).size;
					// Several portfolios are delivered later by the download notification, so Confirm hands them over
					// at once and the export goes on in the background; a single file is waited for.
					const inBackground = portfoliosIn(batchExportConfig) > 1;

					// Only SmartX refuses with a message for the user; a catch of undefined leaves other converters' errors as they are.
					const onRefusal =
						converter === InvestmentExportConverterType.SmartxConverter ? rethrowConversionRefusal : undefined;

					const exportCompositions = () =>
						runWithErrorReporting(
							async () => {
								const integrationsApi = getApiGen(IntegrationsControllerV3ApiFactory);
								let refused: AsOfTriggerResponse[] = [];
								let notReady: string[] = [];

								try {
									// A snapshot is read as soon as it exists, its reports or not, and the reports fill the
									// constraints' actual values: each one is commissioned and waited on first.
									const dated = batchExportConfig.filter((request) => request.asOfDate != null);
									if (asOf != null && dated.length > 0) {
										({ refused, notReady } = await commissionAndAwaitSnapshots(
											dated.map(({ uuid }) => uuid!),
											asOf,
										));
										const left = new Set([...refused.map((response) => response.asOfTrigger?.uuid), ...notReady]);
										batchExportConfig = batchExportConfig.filter(
											(request) => !(request.asOfDate && left.has(request.uuid)),
										);
									}

									const portfolios = portfoliosIn(batchExportConfig);
									if (portfolios > 1) {
										const compositions = await axiosExtract(integrationsApi.batchExportInvestments(batchExportConfig));
										await axiosExtract(integrationsApi.convertTo(converter, compositions, true).catch(onRefusal));
									} else if (portfolios === 1) {
										const compositions = await axiosExtract(integrationsApi.batchExportInvestments(batchExportConfig));
										await downloadContentDisposition(
											await integrationsApi
												.convertTo(converter, compositions, false, { responseType: "blob" })
												.catch(onRefusal),
										);
										if (!inBackground) {
											ctxRef.current.resetMultiselectCtx();
										}
									}
								} catch (error) {
									throw exportFailure(error);
								}
								reportRefusedSnapshots(refused, asOf, "try to download composition as of a date", notReady);
							},
							{
								area: "portfolio",
								attemptedOperation: {
									message: "download multiple portfolio compositions",
									payload: JSON.stringify({
										investments: batchExportConfig,
									}),
								},
							},
						);

					if (inBackground) {
						onActionStarted({ toastTitle: "Download", unavailable, reviewMode: null });
						void exportCompositions().catch(noop);
						return;
					}
					await exportCompositions();
				};

				reviewAsOf(
					"Download: review and confirm",
					// SmartX creates a model for every tab, so a portfolio goes out once: current or proposal.
					{ copy: COMPOSITION_REVIEW_COPY, withoutBoth: converter === InvestmentExportConverterType.SmartxConverter },
					selection,
					unavailable,
					onContinue,
				);
			},
			async exportComposition() {
				const investmentsToValidate = ctxRef.current.investmentsToValidateParam;
				const availabilityResult = checkPortfolioBulkActionAvailability(
					"exportComposition",
					investmentsToValidate,
					executionOpts(),
				);
				const [available, unavailable, proposalReadyInvestments] = splitProposalReady(availabilityResult);
				const selection = { available, proposalReady: proposalReadyInvestments };

				const onContinue = async (state: DownloadReportReviewState): Promise<void> => {
					const { asOf } = state;
					// The data provider takes one snapshot for the whole batch, and under PROPOSAL reads a portfolio
					// without a proposal at its current composition — the plan's own split, in one request.
					const snapshot = effectiveApplyTo(state);
					let investmentUuids = [
						...new Set(planBulkReportPages(selection, state).map(({ investment }) => investment.uuid!)),
					];
					await runWithErrorReporting(
						async () => {
							trackMixPanelEvent("portfolio.bulkActions.exportComposition", {
								selectedPortfolios: investmentUuids.length,
							});
							let refused: AsOfTriggerResponse[] = [];
							let notReady: string[] = [];
							try {
								// The center of the export mirrors the user's workspace columns: prefer the workspace in
								// scope, falling back to the default "Portfolios" workspace when the list wasn't opened from one.
								const workspaceUuid =
									ctx.workspaceUid ?? (await workspaceRepository.getDefaultWorkspace.fetchQuery()).uuid;
								const { workspace, sharedConcepts } = workspaceUuid
									? await parallelize({
											workspace: () =>
												fetchPastCancellation(() => workspaceRepository.getWorkspace.fetchQuery(workspaceUuid)),
											sharedConcepts: () =>
												fetchPastCancellation(() => workspaceRepository.getSharedConcepts.fetchQuery()).then(
													({ sharedConcepts }) => sharedConcepts ?? [],
												),
										})
									: { workspace: undefined, sharedConcepts: [] };
								// The export leaves out a snapshot whose reports are not in yet, so each one is waited on first.
								if (asOf != null && investmentUuids.length > 0) {
									({ refused, notReady } = await commissionAndAwaitSnapshots(investmentUuids, asOf));
									const left = new Set([...refused.map((response) => response.asOfTrigger?.uuid), ...notReady]);
									investmentUuids = investmentUuids.filter((uuid) => !left.has(uuid));
								}
								if (investmentUuids.length > 0) {
									const config = buildBulkCompositionExportConfig({
										investmentUuids,
										snapshot,
										asOf: asOf ?? undefined,
										workspace,
										sharedConcepts,
									});
									const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
									await downloadContentDisposition(file);
									ctxRef.current.resetMultiselectCtx();
								}
							} catch (error) {
								throw new ToastableError("Unable to export investments", {
									cause: error,
									icon: "Portfolio",
								});
							}
							reportRefusedSnapshots(refused, asOf, "try to export composition as of a date", notReady);
						},
						{
							area: "portfolio",
							attemptedOperation: {
								message: "bulk export portfolio compositions",
								payload: JSON.stringify({ investments: investmentUuids, snapshot, asOf }),
							},
						},
					);
				};

				reviewAsOf(
					"Export composition: review and confirm",
					{ copy: COMPOSITION_REVIEW_COPY },
					selection,
					unavailable,
					onContinue,
				);
			},
			async generateCommentary(templateUUID: string) {
				const investmentsToValidate = ctxRef.current.investmentsToValidateParam;
				const availabilityResult = checkPortfolioBulkActionAvailability(
					"generateCommentary",
					investmentsToValidate,
					executionOpts(),
				);
				const [available, unavailable, proposalReadyInvestments] = splitProposalReady(availabilityResult);

				const onContinue = (applyTo: "CURRENT" | "PROPOSAL" | "BOTH"): Promise<void> =>
					runWithErrorReporting(
						async () => {
							trackMixPanelEvent("portfolio.bulkActions.generateCommentary", {
								selectedPortfolios: available.length + proposalReadyInvestments.length,
							});
							try {
								await portfolioRepository.createBulkCommentary(
									switchExpr(applyTo, {
										CURRENT: () => [
											...available.map(({ uuid }) => ({
												investmentUUID: uuid,
												templateUUID,
												proposal: false,
											})),
											...proposalReadyInvestments.map(({ uuid }) => ({
												investmentUUID: uuid,
												templateUUID,
												proposal: false,
											})),
										],
										PROPOSAL: () =>
											proposalReadyInvestments.map(({ uuid }) => ({
												investmentUUID: uuid,
												templateUUID,
												proposal: true,
											})),
										BOTH: () => [
											...available.map(({ uuid }) => ({
												investmentUUID: uuid,
												templateUUID,
												proposal: false,
											})),
											...proposalReadyInvestments.flatMap(({ uuid }) => [
												{
													investmentUUID: uuid,
													templateUUID,
													proposal: false,
												},
												{
													investmentUUID: uuid,
													templateUUID,
													proposal: true,
												},
											]),
										],
									}),
								);
								onActionStarted({
									toastTitle: "Generate commentary",
									unavailable,
									reviewMode: null,
								});
							} catch (error) {
								throw new ToastableError("Unable to generate commentaries", { cause: error, icon: "Portfolio" });
							}
						},
						{ area: "portfolio", attemptedOperation: { message: "try to create commentary" } },
					);

				if (unavailable == null && proposalReadyInvestments.length === 0) {
					await onContinue("CURRENT");
				} else {
					spawnCustomizedBulkAnalysisReviewDialogWithException(
						{
							initialState: "CURRENT" as "CURRENT" | "PROPOSAL" | "BOTH",
							header: "Generate commentary: review and confirm",
							children: (stateStore$) => (
								<BulkAnalysisReviewSections
									bulkActionAvailabilityResult={availabilityResult}
									midSections={proposalMidSections(proposalReadyInvestments.length, stateStore$)}
									getRedirectUrl={ctxRef.current.onOpenNewTab}
								/>
							),
							canConfirm: available.length > 0 || proposalReadyInvestments.length > 0,
							onContinue,
						},
						unavailable,
					).catch(noop);
				}
			},
			useAsMixedPortfolio() {
				const investmentsToValidate = ctxRef.current.investmentsToValidateParam;
				const availabilityResult = checkPortfolioBulkActionAvailability(
					"useAsMixedPortfolio",
					investmentsToValidate,
					executionOpts(),
				);
				const [available, unavailable] = availabilityResult;
				const onContinue = () => {
					trackMixPanelEvent("portfolio.bulkActions.merge", { selectedPortfolios: available.length });
					if (ctx.workspaceUid) {
						push("Portfolios/ManualPortfolioCreationWithWorkspace", {
							workspaceUid: ctx.workspaceUid,
							preselectedInvestments: available.map(({ uuid }) => uuid).join(","),
						});
					} else {
						push("Portfolios/ManualPortfolioCreation", {
							preselectedInvestments: available.map(({ uuid }) => uuid).join(","),
						});
					}
					ctxRef.current.resetMultiselectCtx();
				};

				if (unavailable == null) {
					onContinue();
				} else {
					spawnCustomizedBulkAnalysisReviewDialogWithException(
						{
							initialState: null,
							header: "Merge: review and confirm",
							children: (
								<BulkAnalysisReviewSections
									bulkActionAvailabilityResult={availabilityResult}
									getRedirectUrl={ctxRef.current.onOpenNewTab}
								/>
							),
							canConfirm: available.length > 0,
							onContinue,
						},
						unavailable,
					).catch(noop);
				}
			},
			compare() {
				const investmentsToValidate = ctxRef.current.investmentsToValidateParam;
				const availabilityResult = checkPortfolioBulkActionAvailability(
					"compare",
					investmentsToValidate,
					executionOpts(),
				);
				const [available, unavailable, proposalReadyInvestments] = splitProposalReady(availabilityResult);

				const onContinue = (proposalOrCurrentMap: Map<string, TemplateChooserUserPreferenceValue>): void => {
					trackMixPanelEvent("portfolio.bulkActions.compare", {
						selectedPortfolios: available.length + proposalReadyInvestments.length,
					});
					ctxRef.current.setCompareMultipleInvestments(
						proposalOrCurrentMap.merge(available.map(({ uuid }) => [uuid!, { current: true, enhance: false }])),
					);
					ctxRef.current.resetMultiselectCtx();
				};

				if (unavailable == null && proposalReadyInvestments.length === 0) {
					onContinue(Map());
				} else {
					spawnCustomizedBulkAnalysisReviewDialogWithException(
						{
							initialState: Map</* uuid */ string, TemplateChooserUserPreferenceValue>(),
							header: "Compare: review and confirm",
							children: (stateStore$) => (
								<BulkAnalysisReviewSections
									getRedirectUrl={ctxRef.current.onOpenNewTab}
									bulkActionAvailabilityResult={availabilityResult}
									midSections={[
										...(!proposalReadyInvestments.length
											? []
											: [
													{
														count: proposalReadyInvestments.length,
														title:
															proposalReadyInvestments.length === 1
																? "Portfolio has a pending proposal. Apply the action to:"
																: "Portfolios have a pending proposal. Apply the action to:",
														children: (
															<WithRwAtom atom$={stateStore$}>
																{([state, setState]) => (
																	<TemplateChooserTable
																		proposalReadyInvestments={proposalReadyInvestments}
																		userPreference={state}
																		onChangePreference={setState}
																	/>
																)}
															</WithRwAtom>
														),
													},
												]),
									]}
								/>
							),
							canConfirm: (state) =>
								(proposalReadyInvestments.length > 0 && state.some((x) => x.current || x.enhance)) ||
								available.length > 0,
							onContinue,
						},
						unavailable,
					).catch(noop);
				}
			},
			delete() {
				const investmentsToValidate = ctxRef.current.investmentsToValidateParam;
				const availabilityResult = checkPortfolioBulkActionAvailability(
					"delete",
					investmentsToValidate,
					executionOpts(),
				);
				const [available, unavailable] = availabilityResult;
				const onContinue = () =>
					runWithErrorReporting(
						async () => {
							trackMixPanelEvent("portfolio.bulkActions.delete", { selectedPortfolios: available.length });
							const { failed, succeded } = await multipleEntityDelete({ area: "Portfolios", items: available }, false);

							if (failed.length < available.length) {
								refetchPortfolios();
							}

							if (succeded.length > 0) {
								onActionStarted({
									toastTitle: "Delete",
									toastSubtitle: `You have successfully deleted ${succeded.length} portfolios`,
									unavailable,
									reviewMode: null,
								});
							}
							if (failed.length > 0 && available.length === failed.length) {
								throw new ToastableError(`Unable to delete ${failed.length} portfolios`, {
									icon: "Portfolio",
								});
							}

							if (failed.length > 0) {
								platformToast({
									icon: "Portfolio",
									children: `Unable to delete ${failed.length} portfolios`,
									severity: "error",
								});
							}
						},
						{
							area: "portfolio",
							attemptedOperation: {
								message: "delete multiple portfolios",
								payload: JSON.stringify({
									investments: available,
								}),
							},
						},
					);

				spawnCustomizedBulkAnalysisReviewDialogWithException(
					{
						initialState: null,
						header: "Delete: review and confirm",
						children: (
							<BulkAnalysisReviewSections
								bulkActionAvailabilityResult={availabilityResult}
								getRedirectUrl={ctxRef.current.onOpenNewTab}
							/>
						),
						canConfirm: available.length > 0,
						onContinue,
					},
					unavailable,
				).catch(noop);
				// }
			},
			async editSettings() {
				const intentId = generateIntentId();
				const investmentsToValidate = ctxRef.current.investmentsToValidateParam;
				const availabilityResult = checkPortfolioBulkActionAvailability(
					"editSettings",
					investmentsToValidate,
					executionOpts(),
				);
				const [available] = availabilityResult;

				if (investmentsToValidate.length === 1 && available.length === 1) {
					trackMixPanelEvent("portfolio.bulkActions.editSettings", { intentId, selectedPortfolios: available.length });
					push("Portfolios/SettingsPortfolio", { intentId, portfolioUid: available[0].uuid! });
				} else {
					const onContinue = async () => {
						trackMixPanelEvent("portfolio.bulkActions.editSettings", {
							intentId,
							selectedPortfolios: available.length,
						});
						if (available.length === 1) {
							if (ctx.workspaceUid) {
								push("Portfolios/SettingsPortfolioWithWorkspace", {
									intentId,
									portfolioUid: available[0].uuid!,
									workspaceUid: ctx.workspaceUid,
								});
							} else {
								push("Portfolios/SettingsPortfolio", { intentId, portfolioUid: available[0].uuid! });
							}
							ctxRef.current.resetMultiselectCtx();
						} else {
							await runWithErrorReporting(
								async () => {
									try {
										const { uuid } = await axiosExtract(
											getApiGen(InvestmentBulkBulkStaticConfigurationControllerV1ApiFactory).createDraftConfiguration(
												available.map((x) => x.uuid!),
											),
										);
										if (ctx.workspaceUid) {
											push("Portfolios/SettingsPortfolioMultiWithWorkspace", {
												workspaceUid: ctx.workspaceUid,
												bulkUid: uuid ?? "",
											});
										} else {
											push("Portfolios/SettingsPortfolioMulti", {
												bulkUid: uuid ?? "",
											});
										}
										ctxRef.current.resetMultiselectCtx();
									} catch (error) {
										throw new ToastableError("Unable to edit portfolio settings", {
											cause: error,
											icon: "Portfolio",
										});
									}
								},
								{ area: "portfolio", attemptedOperation: { message: "try to create bulk edit settings" } },
							);
						}
					};

					await reviewThenRun("Edit market view", availabilityResult, onContinue);
				}
			},
			async share() {
				runWithErrorReporting(
					async () => {
						await spawnBulkShareAction({
							reasonKeys: ["ACL_OWNER_REQUIRED"] as const,
							validator: portfolioListActionValidators.share,
							entities: ctxRef.current.investmentsToValidateParam,
							reasonToMessage: PortfolioActionUnavailableReasonToMessageMap,
							labels: { entitySingular: "Portfolio", entityPlural: "Portfolios" },
							getRedirectUrl: ctxRef.current.onOpenNewTab,
							area: "portfolio",
							customerAndLinkedAclProvider: (available) => () =>
								parallelize(available.map((inv) => () => portfolioRepository.getACL.fetchQuery(inv.uuid!))),
							getEntityMetadata: (inv) => ({
								uuid: inv.uuid!,
								name: inv.name!,
								entityType: "INVESTMENT" as const,
							}),
							onIntent: (available) =>
								trackMixPanelEvent("portfolio.bulkActions.share", { selectedPortfolios: available.length }),
							async saveAclHandler(available, { aclByEntity }) {
								const investmentsToShare = available.map(
									(investment): BulkAclEntity => ({
										accessControlList: aclByEntity[investment.uuid!],
										entityUuid: investment.uuid,
									}),
								);
								await portfolioRepository.bulkShare(investmentsToShare);
								refetchPortfolios();
							},
						});
					},
					{ area: "alerts", attemptedOperation: { message: "Bulk action entities error" } },
				);

				ctxRef.current.resetMultiselectCtx();
			},
		} satisfies Record<PortfolioListAction, (...args: any[]) => MaybePromise<void>>;
	}, [ctxRef, push, spawnCustomizedBulkAnalysisReviewDialogWithException, onActionStarted, ctx.workspaceUid]);
}

/** How an export review names what it exports. */
type ReviewCopy = {
	/** The exported thing, singular, as the plan summary counts it. */
	noun: string;
	/** When an undated export is taken, as it follows "exported". */
	undated: string;
};

/** How a dated export review names what it exports, and the sentences beside its picker. */
type AsOfReviewCopy = ReviewCopy & {
	/** The sentence beside the picker while no date is pinned. */
	today: string;
	/** The sentence beside the picker once `day`, already formatted, is pinned. */
	dated(day: string): string;
};

/**
 * A dated copy offers the picker; an undated one reviews the export with no date to pick. `withoutBoth`
 * leaves "Both" out of the proposal choice.
 */
type AsOfReviewMode = XOR<[{ copy: AsOfReviewCopy }, { undatedCopy: ReviewCopy }]> & { withoutBoth?: boolean };

const REPORT_REVIEW_COPY: AsOfReviewCopy = {
	noun: "report",
	undated: "as of today",
	today: "Reports are generated from today's data.",
	dated: (day) => `Reports are taken as of ${day}.`,
};
const COMPOSITION_REVIEW_COPY: AsOfReviewCopy = {
	noun: "composition",
	undated: "as of today",
	today: "Compositions are exported from today's data.",
	dated: (day) => `Compositions are taken as of ${day}.`,
};
// The gateway's generation request carries no date.
const STORYFOLIO_V2_REVIEW_COPY: ReviewCopy = {
	noun: "document",
	undated: "from the latest data",
};

type AsOfReviewBodyProps<T extends InvestmentSummary | InvestmentProjectedListEntry> = {
	stateStore$: RwAtom<DownloadReportReviewState>;
	selection: { available: T[]; proposalReady: T[] };
	unavailable: PortfolioBulkActionAvailabilityResult<T>[1];
	getRedirectUrl(investmentUuids: string[]): string;
} & AsOfReviewMode;

/**
 * The review of a bulk export that can be taken as of a date: the "As of" picker (for a dated copy only),
 * the proposal choice while no date is picked, the portfolios left out, and what Confirm will export.
 */
function AsOfReviewBody<T extends InvestmentSummary | InvestmentProjectedListEntry>({
	stateStore$,
	selection,
	unavailable,
	copy,
	undatedCopy,
	withoutBoth,
	getRedirectUrl,
}: AsOfReviewBodyProps<T>) {
	const { noun, undated } = copy ?? undatedCopy;
	const { available, proposalReady } = selection;
	const everyone = [...available, ...proposalReady];
	// The new tab holds only what the user can act on: a portfolio that did not exist on the date needs no fixing.
	const openUnsuitable =
		unavailable == null
			? undefined
			: () => getRedirectUrl(Object.values(unavailable).flatMap((investments) => investments.map((x) => x.uuid!)));
	return (
		<WithRwAtom atom$={stateStore$}>
			{([state, setState]) => {
				const { asOf } = state;
				// With a date there is no proposal choice: every portfolio that existed on it is exported at it, and
				// the rest are left out. Today, a proposal-ready portfolio is counted by the proposal section instead.
				const atTheDate = asOf != null ? everyone.filter((x) => canBeDated(x, asOf)) : available;
				const notYetIncepted = asOf == null ? [] : everyone.filter((x) => !canBeDated(x, asOf));
				return (
					<Column flexGrow={0}>
						{copy && (
							<>
								<Row gap={16} alignItems="center" flexGrow={0}>
									<AsOfButton
										asOf={asOf ?? undefined}
										setAsOf={(asOf) => setState((prev) => ({ ...prev, asOf }))}
										inceptionDate={earliestInception(everyone)}
										qualifier="BulkAnalysisReviewDialog/AsOfDate"
										label={{ type: "Body/M/Medium" }}
									/>
									<Text as="span" type="Body/M/Book" color={themeCSSVars.palette_N500}>
										{asOf != null ? copy.dated(formatDate(asOf, { timezone: "UTC" })) : copy.today}
									</Text>
								</Row>
								<div className="border-b w-10 my-4" style={{ borderBottomColor: themeCSSVars.palette_N100 }} />
							</>
						)}
						<BulkAnalysisReviewSections
							bulkActionAvailabilityResult={[[], withNotYetIncepted(unavailable, notYetIncepted)]}
							getRedirectUrl={openUnsuitable}
							midSections={[
								...atTheDateMidSection(atTheDate.length, asOf, undated),
								...proposalMidSection(
									asOf == null ? proposalReady.length : 0,
									proposalChoiceRadios(
										state.applyTo,
										(applyTo) => setState((prev) => ({ ...prev, applyTo })),
										withoutBoth,
									),
								),
							]}
						/>
						<div className="border-b w-10 my-4" style={{ borderBottomColor: themeCSSVars.palette_N100 }} />
						<Text as="div" type="Body/M/Medium" data-qualifier="BulkAnalysisReviewDialog/PlanSummary">
							{summariseBulkReportPlan(
								planBulkReportPages(selection, state),
								selection,
								(day) => formatDate(day, { timezone: "UTC" }),
								noun,
								undated,
							)}
						</Text>
					</Column>
				);
			}}
		</WithRwAtom>
	);
}

/** How long a composition export waits for the snapshots it commissioned: the PDF render job's budget for a page. */
const SNAPSHOT_WAIT = seconds(90);

/**
 * Commissions, in one call, the snapshot at `asOf` of every portfolio in `uuids`, and returns the triggers
 * the backend refused and the portfolios whose snapshot is still generating.
 */
async function commissionSnapshots(
	uuids: string[],
	asOf: string,
): Promise<{ refused: AsOfTriggerResponse[]; generating: string[] }> {
	const responses = await axiosExtract(
		getApiGen(IntegrationsImportAsOfTriggerControllerApiFactory).bulkInsert(
			uuids.map((uuid) => ({ uuid, asOfDate: asOf })),
		),
	);
	return {
		refused: responses.filter((response) => response.error != null),
		// `status` is optional in the generated response: an answer that does not say READY is waited on.
		generating: responses.flatMap(({ error, status, asOfTrigger }) =>
			error == null && status !== AsOfTriggerResponseStatusEnum.Ready && asOfTrigger?.uuid ? [asOfTrigger.uuid] : [],
		),
	};
}

/**
 * {@link commissionSnapshots}, then a wait of up to {@link SNAPSHOT_WAIT} from the call for the reports of
 * every snapshot still generating. Listening starts before the call, so reports that
 * land before it answers are not missed. Returns the refused triggers and the portfolios not ready in time.
 */
async function commissionAndAwaitSnapshots(
	uuids: string[],
	asOf: string,
): Promise<{ refused: AsOfTriggerResponse[]; notReady: string[] }> {
	const stop = new AbortController();
	const timeout = setTimeout(() => stop.abort(), SNAPSHOT_WAIT);
	const reportsIn: Record<string, Promise<boolean>> = Object.fromEntries(
		uuids.map((uuid) => [
			uuid,
			waitForEvent("investment-report-update", {
				filter: (payload) => payload.uuid === uuid && payload.asOfDate === asOf,
				signal: stop.signal,
			}).then(
				() => true,
				() => false,
			),
		]),
	);
	try {
		const { refused, generating } = await commissionSnapshots(uuids, asOf);
		if (generating.length === 0) {
			return { refused, notReady: [] };
		}
		const ready = await parallelize(generating.map((uuid) => () => reportsIn[uuid] ?? false));
		return { refused, notReady: generating.filter((_, i) => !ready[i]) };
	} finally {
		clearTimeout(timeout);
		stop.abort();
	}
}

/**
 * Reads a query, asking once more when the read was cancelled: an invalidation refetches a live query and
 * cancels a fetch of it still in flight, and the second ask joins that refetch.
 */
async function fetchPastCancellation<T>(fetch: () => Promise<T>): Promise<T> {
	try {
		return await fetch();
	} catch (error) {
		if (!isCancelledError(error)) {
			throw error;
		}
		return fetch();
	}
}

function proposalChoiceRadios(value: ApplyTo, onChange: (value: ApplyTo) => void, withoutBoth = false): ReactNode {
	return (
		<RadioGroup value={value} onChange={onChange}>
			<Column flexGrow={0} gap={8}>
				<Radio value="CURRENT">Only current</Radio>
				<Radio value="PROPOSAL">Only proposal</Radio>
				{!withoutBoth && <Radio value="BOTH">Both</Radio>}
			</Column>
		</RadioGroup>
	);
}

function proposalMidSection(nOfProposalReadyInvestments: number, children: ReactNode) {
	return !nOfProposalReadyInvestments
		? []
		: [
				{
					count: nOfProposalReadyInvestments,
					title:
						nOfProposalReadyInvestments === 1
							? "Portfolio has a pending proposal. Apply the action to:"
							: "Portfolios have a pending proposal. Apply the action to:",
					children,
				},
			];
}

function proposalMidSections(nOfProposalReadyInvestments: number, stateStore$: RwAtom<ApplyTo>) {
	return proposalMidSection(
		nOfProposalReadyInvestments,
		<WithRwAtom atom$={stateStore$}>{([state, setState]) => proposalChoiceRadios(state, setState)}</WithRwAtom>,
	);
}

/**
 * The portfolios whose current report Confirm will produce, and when it is taken: at `asOf`, or as
 * `undated` says while no date is set. Rendered here rather than through `BulkAnalysisReviewSections`'
 * own leading section, whose "ready to be processed" is a claim about eligibility that every other bulk
 * action shares and that no date can qualify.
 */
function atTheDateMidSection(count: number, asOf: string | null, undated: string) {
	if (!count) {
		return [];
	}
	const when = asOf == null ? undated : `as of ${formatDate(asOf, { timezone: "UTC" })}`;
	return [
		{
			count,
			title: count === 1 ? `Portfolio will be exported ${when}` : `Portfolios will be exported ${when}`,
			palette: "success" as const,
		},
	];
}

/** No portfolio held back for any reason: the record a reason check starts from. */
function noUnavailable<T>(): Record<PortfolioActionUnavailableReason, T[]> {
	return Object.fromEntries(Object.keys(PortfolioActionUnavailableReason).map((k) => [k, [] as T[]])) as Record<
		PortfolioActionUnavailableReason,
		T[]
	>;
}

/** The portfolios left out of the selection, joined by the ones that did not exist on the picked date. */
function withNotYetIncepted<T>(
	unavailable: Record<PortfolioActionUnavailableReason, T[]> | null,
	notYetIncepted: T[],
): Record<PortfolioActionUnavailableReason, T[]> | null {
	if (notYetIncepted.length === 0) {
		return unavailable;
	}
	return { ...(unavailable ?? noUnavailable<T>()), NOT_YET_INCEPTED: notYetIncepted };
}

function splitProposalReady<T extends InvestmentSummary | InvestmentProjectedListEntry>(
	availabilityResult: PortfolioBulkActionAvailabilityResult<T>,
) {
	const [available] = availabilityResult;

	const proposalReadyInvestments = availabilityResult[1]?.PROPOSAL_READY ?? [];
	if (availabilityResult[1]) {
		availabilityResult[1].PROPOSAL_READY = [];
		if (Object.values(availabilityResult[1]).every((list) => list.length === 0)) {
			availabilityResult[1] = null;
		}
	}
	const unavailable = availabilityResult[1];

	return [available, unavailable, proposalReadyInvestments] as const;
}
